import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import pluralize from 'pluralize';

import {
  Checkbox,
  DropdownMenuItem,
  Dropdown,
  NewButton,
  Icon,
  Divider
} from '../../../../../components';
import { CreateSpaceForm } from '../../atoms';
import { useDropShadow } from '../../../../../hooks';
import { spaceShape } from '../../../../../propsShape';

const SelectSpaceDropdownContainer = styled.div`
  display: flex;
`;

const DropdownMenuItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 200px;
  overflow-y: scroll;
`;

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  &:hover {
    background-color: transparent;
  }
`;

const StyledNewButton = styled(NewButton)`
  padding: 0;
  column-gap: 0;
`;

const ButtonInnerPadding = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
  padding: 8px;
  white-space: nowrap;
`;

const StyledCreateSpaceForm = styled(CreateSpaceForm)`
  box-shadow: ${({ $dropShadow }) => ($dropShadow ? '0px -4px 8px var(--gray-8)' : 'none')};

  transition: box-shadow 100ms linear;
`;

const SelectSpaceDropdown = ({
  spaces,
  defaultSelectedSpaceIds = [],
  onAddQuestionToSpace,
  onRemoveQuestionFromSpace,
  onCreateSpace
}) => {
  const spacesRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [localSpaces, setLocalSpaces] = useState(spaces);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState(defaultSelectedSpaceIds);
  const { dropShadow } = useDropShadow(spacesRef);

  useEffect(() => {
    setLocalSpaces(spaces);
  }, [spaces]);

  useEffect(() => {
    setSelectedSpaceIds(defaultSelectedSpaceIds);
  }, [defaultSelectedSpaceIds]);

  const handleCheckboxToggle = useCallback(
    async (space, evt) => {
      if (evt) {
        evt.stopPropagation();
      }
      setIsLoading(true);
      if (selectedSpaceIds.includes(space.id)) {
        await onRemoveQuestionFromSpace(space);
        setSelectedSpaceIds(() => selectedSpaceIds.filter((s) => s !== space.id));
      } else {
        await onAddQuestionToSpace(space);
        setSelectedSpaceIds(() => [...selectedSpaceIds, space.id]);
      }
      setIsLoading(false);
    },
    [
      selectedSpaceIds,
      setSelectedSpaceIds,
      setIsLoading,
      onAddQuestionToSpace,
      onRemoveQuestionFromSpace
    ]
  );

  const handleCreateSpace = useCallback(
    async (title) => {
      setIsLoading(true);
      const newSpace = await onCreateSpace(title);
      if (newSpace) {
        setLocalSpaces(() => [...localSpaces, newSpace]);
        await handleCheckboxToggle(newSpace, null);
      }
    },
    [handleCheckboxToggle, localSpaces, setSelectedSpaceIds, setIsLoading]
  );

  const toggleText = useMemo(() => {
    if (isLoading) {
      return 'Saving';
    }

    if (selectedSpaceIds.length === 0) {
      return 'Save';
    }

    if (selectedSpaceIds.length === 1) {
      const space = localSpaces.find((s) => s.id === selectedSpaceIds[0]);
      if (!space) {
        return 'Save';
      }
      return `Saved to: ${space.title}`;
    }

    const selectedSpaces = localSpaces.reduce((acc, space) => {
      if (selectedSpaceIds.includes(space.id)) {
        return acc + 1;
      }
      return acc;
    }, 0);

    return `Saved to: ${selectedSpaces} ${pluralize('spaces', selectedSpaces)}`;
  }, [localSpaces, selectedSpaceIds, isLoading]);

  const toggleIcon = useMemo(() => {
    if (isLoading) {
      return <Icon size={48} displaySize={20} name='spinner' color='var(--primary-default)' />;
    }

    const selectedSpaces = localSpaces.reduce((acc, space) => {
      if (selectedSpaceIds.includes(space.id)) {
        return acc + 1;
      }
      return acc;
    }, 0);

    if (selectedSpaces === 0) {
      return <Icon displaySize={20} name='bookmark' />;
    }

    return <Icon size={20} name='bookmark-fill' color='var(--primary-default)' />;
  }, [selectedSpaceIds, isLoading]);

  return (
    <SelectSpaceDropdownContainer>
      <Dropdown
        menuPlacement='bottom-end'
        menuStyle={{ width: 250, padding: 0, overflow: 'hidden' }}
        toggleComponent={
          <StyledNewButton>
            <ButtonInnerPadding>
              {toggleIcon} {toggleText}
            </ButtonInnerPadding>
            <Divider vertical padding={0} color='var(--border-muted)' />
            <ButtonInnerPadding>
              <Icon size={20} name='chevron-down' />
            </ButtonInnerPadding>
          </StyledNewButton>
        }
      >
        {() => (
          <>
            <DropdownMenuItemsContainer ref={spacesRef}>
              {localSpaces.map((space) => (
                <StyledDropdownMenuItem
                  key={space.id}
                  shouldCloseMenu={false}
                  onClick={(evt) => handleCheckboxToggle(space, evt)}
                  leftIcon={
                    <Checkbox
                      onChange={({ evt }) => handleCheckboxToggle(space, evt)}
                      value={space.id}
                      checked={selectedSpaceIds.includes(space.id)}
                    />
                  }
                >
                  {space.title}
                </StyledDropdownMenuItem>
              ))}
            </DropdownMenuItemsContainer>
            <StyledCreateSpaceForm
              spaces={localSpaces}
              onSubmit={handleCreateSpace}
              $dropShadow={dropShadow}
            />
          </>
        )}
      </Dropdown>
    </SelectSpaceDropdownContainer>
  );
};

SelectSpaceDropdown.propTypes = {
  spaces: PropTypes.arrayOf(spaceShape).isRequired,
  defaultSelectedSpaceIds: PropTypes.arrayOf(PropTypes.string),
  onAddQuestionToSpace: PropTypes.func.isRequired,
  onRemoveQuestionFromSpace: PropTypes.func.isRequired,
  onCreateSpace: PropTypes.func.isRequired
};

export { SelectSpaceDropdown };
